import {
  CONTAINER_OPTIONS,
  CONTENT_LAYOUT_OPTIONS,
  HEADING_ALIGNMENT_OPTIONS,
  MODULE_FILL_TYPE_OPTIONS,
  MODULE_FLOW_DIRECTION_OPTIONS,
  TITLE_SIZE_OPTIONS,
  TITLE_TYPE_OPTIONS,
} from '@/common/constants';
import { useModuleStyles } from '@/common/hooks';
import { BaseModuleProps } from '@/common/types';
import { CTABlock } from '@/components/blocks/CTABlock/CTABlock';
import { HeadingBlock, HeadingBlockProps } from '@/components/blocks/HeadingBlock/HeadingBlock';
import React from 'react';

import { CTAProps } from '@/components/CTA';
import { Typography } from '@eventbrite/marmalade';
import styles from './ComparisonTable.module.scss';
import { TableCellType } from './ComparisonTableTypes';
import { TableDesktop } from './TableDesktop/TableDesktop';
import { TableMobile } from './TableMobile/TableMobile';

export interface ComparisonTableProps extends BaseModuleProps {
  /** The comparison table data structure */
  comparisonTable: TableCellType[][];
  /** The paragraph text */
  paragraph?: string;
  /**TableCTA */
  tableCTA?: CTAProps[];
  /** The CTA caption */
  tableCtaCaption?: string;
}
/**
 * ComparisonTable module component
 * @param props - The props for the ComparisonTable module
 * @returns The ComparisonTable module component
 */
export const ComparisonTable: React.FC<ComparisonTableProps> = ({
  anchorTag = undefined,
  container = CONTAINER_OPTIONS.CONTAINED,
  fillType = MODULE_FILL_TYPE_OPTIONS.BASE,
  gradientFillToken,
  layout = CONTENT_LAYOUT_OPTIONS.CONTENT_TRAILING,
  flowDirection = MODULE_FLOW_DIRECTION_OPTIONS.COLUMN,
  eyebrow,
  title,
  titleSize = TITLE_SIZE_OPTIONS.LARGE,
  titleType = TITLE_TYPE_OPTIONS.H2,
  bodyRichTextAsHTML,
  alignment = HEADING_ALIGNMENT_OPTIONS.CENTER,
  ctas,
  caption,
  comparisonTable,
  paragraph,
  tableCTA,
  tableCtaCaption,
}) => {
  const headingConfig: HeadingBlockProps = {
    eyebrow,
    title,
    titleSize,
    titleType,
    bodyRichTextAsHTML,
    alignment,
    ctas,
    caption,
  };

  // Use the common module styles hook
  const { sectionStyles, moduleBaseStyles } = useModuleStyles({
    container,
    fillType,
    gradientFillToken,
    layout,
    flowDirection,
    moduleClassName: 'module-comparison-table',
    styles,
  });

  // Determine number of columns (assume all rows have same length as header)
  const numCols = comparisonTable && comparisonTable[0] ? comparisonTable[0].length : 0;

  return (
    <section className={sectionStyles} id={anchorTag}>
      <div className={moduleBaseStyles}>
        <HeadingBlock {...headingConfig} />
        <div
          className={
            numCols == 3 ? `${styles.desktop_table} ${styles.dual_table}` : styles.desktop_table
          }
        >
          <TableDesktop data={comparisonTable} />
        </div>
        {/* Only render TableMobile if more than 2 columns */}
        {numCols > 3 && (
          <div className={styles.mobile_table}>
            <TableMobile data={comparisonTable} />
          </div>
        )}
        {paragraph && (
          <Typography as="p" className={styles.paragraph_text}>
            {paragraph}
          </Typography>
        )}
        {tableCTA && (
          <div className={styles.table_cta_block}>
            <CTABlock caption={tableCtaCaption || ''} ctas={tableCTA} />
          </div>
        )}
      </div>
    </section>
  );
};
